import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getApps, initializeApp } from 'firebase/app';
import { Capacitor } from '@capacitor/core';

let foregroundListenerSetup = false;

const isPushSubscribed = ref(false);
const isPushSupported = ref(false);
const isPushLoading = ref(false);
const isPushAvailableAfterInstall = ref(false);

const NATIVE_PUSH_TOKEN_KEY = 'nativePushToken';

export function usePushNotifications() {
  const config = useRuntimeConfig();
  const api = useApi();
  const { isIos, isInstalled } = usePwaInstall();

  const isNative = Capacitor.isNativePlatform();

  // --- Web: Firebase messaging ---

  const getFirebaseMessaging = () => {
    if (!config.public.firebase.apiKey) {
      throw new Error('Firebase is not configured. Set firebase.config.ts values and restart the dev server.');
    }
    const app = getApps().length ? getApps()[0] : initializeApp(config.public.firebase);
    const messaging = getMessaging(app);

    if (!foregroundListenerSetup) {
      foregroundListenerSetup = true;
      onMessage(messaging, async (payload) => {
        const title = payload.notification?.title || 'opebo';
        const body = payload.notification?.body || '';
        const registration = await navigator.serviceWorker.ready;
        registration.showNotification(title, {
          body,
          icon: '/pwa-192x192.png',
          badge: '/push-badge.png',
          data: { url: payload.data?.url || '/' },
        });
      });
    }

    return messaging;
  };

  // --- Support check ---

  const checkPushSupport = () => {
    if (import.meta.server) return false;

    if (isNative) {
      isPushSupported.value = true;
      isPushAvailableAfterInstall.value = false;
      return true;
    }

    const hasWebPushApi = 'serviceWorker' in navigator && 'Notification' in window;
    isPushSupported.value = hasWebPushApi;

    if (isIos.value && !isInstalled.value) {
      isPushSupported.value = false;
      isPushAvailableAfterInstall.value = true;
    } else {
      isPushAvailableAfterInstall.value = false;
    }

    return isPushSupported.value;
  };

  // --- Subscription check ---

  const checkPushSubscription = async () => {
    if (!checkPushSupport()) return false;

    if (isNative) {
      const token = localStorage.getItem(NATIVE_PUSH_TOKEN_KEY);
      if (!token) {
        isPushSubscribed.value = false;
        return false;
      }
      const { data } = await api.get<{ subscribed: boolean }>(`/push/check?token=${encodeURIComponent(token)}`);
      const isSubscribed = data.value?.subscribed ?? false;
      isPushSubscribed.value = isSubscribed;
      return isSubscribed;
    }

    const registration = await navigator.serviceWorker.ready;
    const messaging = getFirebaseMessaging();

    let token: string;
    try {
      token = await getToken(messaging, {
        vapidKey: config.public.firebase.vapidKey,
        serviceWorkerRegistration: registration,
      });
    } catch {
      isPushSubscribed.value = false;
      return false;
    }

    if (!token) {
      isPushSubscribed.value = false;
      return false;
    }

    const { data } = await api.get<{ subscribed: boolean }>(`/push/check?token=${encodeURIComponent(token)}`);
    const isSubscribed = data.value?.subscribed ?? false;

    isPushSubscribed.value = isSubscribed;
    return isSubscribed;
  };

  // --- Subscribe ---

  const subscribeToPush = async (): Promise<boolean> => {
    if (!checkPushSupport()) return false;
    if (!navigator.onLine) return false;

    isPushLoading.value = true;

    if (isNative) {
      try {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

        const { receive } = await FirebaseMessaging.requestPermissions();
        if (receive !== 'granted') {
          isPushLoading.value = false;
          return false;
        }

        const { token } = await FirebaseMessaging.getToken();
        if (!token) {
          isPushLoading.value = false;
          return false;
        }

        localStorage.setItem(NATIVE_PUSH_TOKEN_KEY, token);
        await api.post('/push/subscribe', { token });
        isPushSubscribed.value = true;
        isPushLoading.value = false;
        return true;
      } catch (error) {
        console.error('Push subscription failed:', error);
        isPushLoading.value = false;
        return false;
      }
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        isPushLoading.value = false;
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const messaging = getFirebaseMessaging();

      const token = await getToken(messaging, {
        vapidKey: config.public.firebase.vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (!token) {
        isPushLoading.value = false;
        return false;
      }

      await api.post('/push/subscribe', { token });

      isPushSubscribed.value = true;
      isPushLoading.value = false;
      return true;
    } catch (error) {
      console.error('Push subscription failed:', error);
      isPushLoading.value = false;
      return false;
    }
  };

  // --- Unsubscribe (backend only, token stays registered) ---

  const unsubscribeFromPushBackendOnly = async (): Promise<void> => {
    if (!checkPushSupport()) return;

    if (isNative) {
      const token = localStorage.getItem(NATIVE_PUSH_TOKEN_KEY);
      if (token) {
        await api.delete(`/push/unsubscribe?token=${encodeURIComponent(token)}`);
      }
      isPushSubscribed.value = false;
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const messaging = getFirebaseMessaging();

    try {
      const token = await getToken(messaging, {
        vapidKey: config.public.firebase.vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        await api.delete(`/push/unsubscribe?token=${encodeURIComponent(token)}`);
      }
    } catch {
      // ignore errors during backend-only unsubscribe
    }

    isPushSubscribed.value = false;
  };

  // --- Unsubscribe (full, removes token from device + backend) ---

  const unsubscribeFromPush = async (): Promise<boolean> => {
    if (!checkPushSupport()) return false;

    isPushLoading.value = true;

    if (isNative) {
      try {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

        const token = localStorage.getItem(NATIVE_PUSH_TOKEN_KEY);
        if (token) {
          await api.delete(`/push/unsubscribe?token=${encodeURIComponent(token)}`);
          localStorage.removeItem(NATIVE_PUSH_TOKEN_KEY);
        }

        await FirebaseMessaging.deleteToken();
        isPushSubscribed.value = false;
        isPushLoading.value = false;
        return true;
      } catch (error) {
        console.error('Push unsubscribe failed:', error);
        isPushLoading.value = false;
        return false;
      }
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const messaging = getFirebaseMessaging();

      const token = await getToken(messaging, {
        vapidKey: config.public.firebase.vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        await api.delete(`/push/unsubscribe?token=${encodeURIComponent(token)}`);
      }

      isPushSubscribed.value = false;
      isPushLoading.value = false;
      return true;
    } catch (error) {
      console.error('Push unsubscribe failed:', error);
      isPushLoading.value = false;
      return false;
    }
  };

  return {
    isPushSubscribed: readonly(isPushSubscribed),
    isPushSupported: readonly(isPushSupported),
    isPushLoading: readonly(isPushLoading),
    isPushAvailableAfterInstall: readonly(isPushAvailableAfterInstall),
    checkPushSupport,
    checkPushSubscription,
    subscribeToPush,
    unsubscribeFromPush,
    unsubscribeFromPushBackendOnly,
  };
}
